import { useEffect } from 'react'
import { Container, Typography, Box } from '@mui/material'
import Navbar from '../../components/nav/Navbar'
import Footer from '../../components/layout/Footer'

function TermsOfService() {
    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant'
        })
    }, [])

    return (
        <>
            <Navbar />
            <Container component="main" maxWidth="md">
                <Box component="section" mt={4.2}>
                    <Typography component="h1" variant="h4" gutterBottom>
                        Terms of Service
                    </Typography>

                    <Typography component="p" variant="subtitle1">
                        <strong>Effective Date:</strong> September 7th, 2026
                    </Typography>
                    <Typography component="p" variant="subtitle1">
                        Welcome to Vibio! These Terms of Service ("Terms") govern your access to and use of the Vibio
                        platform, website, and related services (collectively, the "Service"), operated by Vibio
                        ("Vibio," "we," "our," or "us"). Please read these Terms carefully. By creating an account or
                        by accessing or using the Service, you agree to be bound by these Terms and by our Privacy
                        Policy. If you do not agree, you may not use the Service.
                    </Typography>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            1. Acceptance and Changes to These Terms
                        </Typography>
                        <Typography component="p" variant="body1">
                            By accessing or using the Service, you acknowledge that you have read, understood, and
                            agree to be bound by these Terms. We may revise these Terms from time to time. If we make
                            material changes, we will provide reasonable notice, for example by email or by a
                            notice within the Service. Your continued use of the Service after the revised Terms take
                            effect constitutes acceptance of the revised Terms. If you do not agree to the revised
                            Terms, you must stop using the Service.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            2. Description of the Service
                        </Typography>
                        <Typography component="p" variant="body1">
                            Vibio is a video-integrated link-in-bio tool. It allows you to create a public profile page
                            featuring a video that plays automatically in a loop, together with links to your social
                            accounts, calendars, websites, and other destinations. The Service provides audience analytics for your page, shareable performance reports,
                            and related creator tools. We encourage you to get creative with the content you publish on
                            your Vibio page, within the rules set out in these Terms.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            3. Eligibility and Your Account
                        </Typography>
                        <ul>
                            <Typography component="li" variant="body1">
                                You must be at least 16 years old to use the Service.
                            </Typography>
                            <Typography component="li" variant="body1">
                                You may register by signing in with a supported identity provider such as
                                Google. You agree to provide accurate, current, and complete information when creating
                                your account and to keep it up to date.
                            </Typography>
                            <Typography component="li" variant="body1">
                                You are responsible for maintaining the confidentiality of your login credentials and
                                for all activity that occurs under your account. Notify us promptly at{' '}
                                <a href="mailto:info@vibio.co">info@vibio.co</a> if you suspect unauthorized use of
                                your account.
                            </Typography>
                            <Typography component="li" variant="body1">
                                You may maintain only one account per person unless we expressly permit otherwise.
                            </Typography>
                        </ul>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            4. Acceptable Use
                        </Typography>
                        <Typography component="p" variant="body1">
                            You are responsible for the content you upload and the way you use the Service. You agree
                            that you will not:
                        </Typography>
                        <ul>
                            <Typography component="li" variant="body1">
                                Upload or share content that infringes the intellectual property, privacy, or other
                                rights of any third party, or that violates any applicable law.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Explicit content:</strong> upload explicit or pornographic images or videos, or
                                add links that navigate to adult websites. This is prohibited under all circumstances.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Hate speech and harmful content:</strong> upload or share content that promotes
                                hate speech, harassment, violence, discrimination, self-harm, or other harmful or
                                illegal activities.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Use the Service for any illegal, fraudulent, or unauthorized purpose, or interfere with
                                or disrupt the integrity or performance of the Service.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Metric manipulation:</strong> artificially inflate or distort analytics data —
                                including views, watch time, or link clicks — through bots, automation, paid engagement
                                schemes, or any other form of non-genuine traffic. Because Vibio analytics and reports
                                may be shared with sponsors and other third parties, manipulating metrics is treated as
                                a serious violation of these Terms.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Attempt to probe, scan, or test the vulnerability of the Service, or circumvent any
                                security or access controls, except as expressly authorized by us in writing.
                            </Typography>
                        </ul>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            5. Your Content and Intellectual Property
                        </Typography>
                        <Typography component="p" variant="body1">
                            Any video, profile image, link, or other material you publish on your Vibio page ("Content")
                            will be publicly visible. You retain full ownership of your Content. So that we can operate
                            the Service, you grant Vibio a non-exclusive, worldwide, royalty-free license to host,
                            store, reproduce, encode, transcode, and technically adapt your Content, and to display and
                            distribute it publicly through the Service and through the infrastructure and
                            content-delivery providers we use to operate it. This license exists solely for the purpose
                            of operating, improving, and promoting your use of the Service, and it ends when you delete
                            the Content or your account, except to the extent copies remain in routine backups for a
                            limited period.
                        </Typography>
                        <Typography component="p" variant="body1" mt={1.5}>
                            The Service itself — including its software, design, and branding — is owned by Vibio and
                            protected by intellectual property laws. We reserve the right to remove any Content that we
                            reasonably believe violates these Terms or applicable law.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            6. Copyright Complaints
                        </Typography>
                        <Typography component="p" variant="body1">
                            We respect the intellectual property rights of others. If you believe that content on the
                            Service infringes your copyright, please send a notice to{' '}
                            <a href="mailto:info@vibio.co">info@vibio.co</a> including: (a) identification of the
                            copyrighted work, (b) the URL of the allegedly infringing content, (c) your contact
                            details, and (d) a statement that you have a good-faith belief the use is not authorized.
                            We will review complaints promptly and remove infringing content where appropriate.
                            First or repeated infringement may result in account termination.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            7. Analytics and Reports
                        </Typography>
                        <Typography component="p" variant="body1">
                            Vibio provides analytics about visits to your page, such as views,
                            approximate watch time, link clicks, traffic sources, device types, and approximate visitor
                            geography. Analytics are provided on an "as available" basis: figures are estimates, may be
                            affected by ad blockers, network conditions, and our automated bot filtering, and are not
                            guaranteed to be complete or error-free. Earnings-related figures shown in the Service are
                            calculated from values you enter yourself and are estimates only — they do not constitute
                            financial, accounting, or tax advice.
                        </Typography>
                        <Typography component="p" variant="body1" mt={1.5}>
                            The Service may allow you to generate shareable reports containing your aggregated
                            analytics. You are solely responsible for how and with whom you share such reports,
                            including any representations you make to sponsors or other third parties based on them.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            8. Plans, Subscriptions, and Billing
                        </Typography>
                        <ul>
                            <Typography component="li" variant="body1">
                                <strong>Paid plan.</strong> Vibio is a paid service and does not offer a free plan.
                                Creating an account and completing initial setup are free, but an active paid
                                subscription — which begins with the free trial described below — is required to use
                                the Service, including managing your page and accessing analytics. The subscription is
                                available on monthly and yearly billing periods, and the features it includes are
                                described within the Service and may evolve over time.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Payment processing.</strong> Payments are processed by our payment provider,
                                Stripe. By purchasing a subscription you also agree to Stripe's applicable terms. Vibio
                                does not store your full card details.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Free trial.</strong> Paid plans may include a free trial (currently 14 days).
                                Unless you cancel before the trial ends, your subscription begins and the applicable
                                fee is charged automatically when the trial expires.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Automatic renewal.</strong> Subscriptions renew automatically at the end of
                                each billing period until cancelled. You can cancel at any time from your account
                                settings; cancellation takes effect at the end of the current billing period, and you
                                retain access to paid features until then.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Downgrades.</strong> When a subscription ends — through cancellation, expiry,
                                or failed payment — your account becomes inactive until you renew. Your public Vibio
                                page remains online, but you lose access to paid features, including editing your page
                                content and full analytics. Your underlying data is not deleted by the downgrade
                                itself, and access is restored when you resubscribe.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Refunds.</strong> Except where required by applicable law, fees are
                                non-refundable and no refunds or credits are provided for partial billing periods.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Price changes.</strong> We may change our prices. If we do, we will notify you
                                in advance, and the new price will apply from your next billing period. If you do not
                                agree with a price change, you may cancel before it takes effect.
                            </Typography>
                        </ul>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            9. Changes to the Service
                        </Typography>
                        <Typography component="p" variant="body1">
                            We are continuously improving Vibio and may modify, update, or discontinue features of the
                            Service at our discretion. Where a change materially reduces the functionality of a paid
                            plan, we will use reasonable efforts to notify you in advance. We are not liable for
                            temporary interruptions, maintenance windows, or changes made in the ordinary course of
                            operating the Service.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            10. Termination
                        </Typography>
                        <Typography component="p" variant="body1">
                            You may stop using the Service and delete your account at any time. We may suspend or
                            terminate your access to the Service, with or without notice, if you breach these Terms —
                            including by uploading prohibited content, engaging in metric manipulation, or otherwise
                            harming the Service or its users — or where we are required to do so by law. Upon
                            termination, your right to use the Service ceases, your public page is taken offline, any
                            active subscription ends in accordance with Section 8, and your Content will be deleted in
                            accordance with our Privacy Policy. Sections of these Terms that by their nature should
                            survive termination (including Sections 5, 11, 12, and 13) will survive.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            11. Disclaimer of Warranties
                        </Typography>
                        <Typography component="p" variant="body1">
                            The Service is provided "as is" and "as available." To the maximum extent permitted by
                            applicable law, Vibio disclaims all warranties, whether express, implied, or statutory,
                            including warranties of merchantability, fitness for a particular purpose, and
                            non-infringement. We do not warrant that the Service will be uninterrupted, secure, or
                            error-free, or that analytics data will be accurate or complete. Nothing in these Terms
                            excludes warranties or rights that cannot be excluded under applicable law, including
                            mandatory consumer protection rights.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            12. Limitation of Liability
                        </Typography>
                        <Typography component="p" variant="body1">
                            To the maximum extent permitted by applicable law, Vibio will not be liable for any
                            indirect, incidental, special, consequential, or punitive damages, or for any loss of
                            profits, revenue, data, or goodwill, arising out of or in connection with your use of the
                            Service. To the same extent, Vibio's total aggregate liability for all claims relating to
                            the Service will not exceed the amount you paid to Vibio in the twelve (12) months
                            preceding the event giving rise to the claim, or fifty U.S. dollars (US$50) if you have not
                            made any payments. Nothing in this section limits liability for fraud, willful misconduct, gross
                            negligence, or any other liability that cannot be limited under applicable law.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            13. Indemnification
                        </Typography>
                        <Typography component="p" variant="body1">
                            You agree to indemnify and hold Vibio harmless from any claims, damages, liabilities, and
                            reasonable expenses (including legal fees) arising from your Content, your use of the
                            Service in violation of these Terms, or your infringement of any third-party rights.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            14. General
                        </Typography>
                        <Typography component="p" variant="body1">
                            If any provision of these Terms is held to be unenforceable, the remaining provisions will
                            remain in full force and effect. Our failure to enforce any right or provision is not a
                            waiver of that right or provision. You may not assign these Terms without our prior written
                            consent; we may assign them in connection with a merger, acquisition, or sale of assets.
                            These Terms, together with the Privacy Policy, constitute the entire agreement between you
                            and Vibio regarding the Service.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3} mb={6}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            15. Contact Us
                        </Typography>
                        <Typography component="p" variant="body1">
                            If you have any questions regarding these Terms, please contact us at{' '}
                            <a href="mailto:info@vibio.co">info@vibio.co</a>.
                        </Typography>
                    </Box>
                </Box>
            </Container>
            <Footer />
        </>
    )
}

export default TermsOfService
